import React, { useContext } from "react";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImageValidateSize from "filepond-plugin-image-validate-size";
import FilePondPluginImageCrop from "filepond-plugin-image-crop";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginImageResize from "filepond-plugin-image-resize";
import FilePondPluginImageTransform from "filepond-plugin-image-transform";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import endpoints from "../endpoints";
import { s2Url } from "@seabrookstudios/types";
import { MyDoneGames, MyGameEditions } from "@seabrookstudios/pitch2table";
import { CachedTokenContext } from "@seabrookstudios/auth";
import { usePreSignedUploadImage, useUploadImage } from "../users/queries";

registerPlugin(FilePondPluginImagePreview);
registerPlugin(FilePondPluginImageValidateSize);
registerPlugin(FilePondPluginImageCrop);
registerPlugin(FilePondPluginFileValidateType);
registerPlugin(FilePondPluginImageResize);
registerPlugin(FilePondPluginImageTransform);
registerPlugin(FilePondPluginFileValidateSize);

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").GameEditionId} props.editionId
 */
export const UploadGameEditionCover = ({ gameId, editionId }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const token = useContext(CachedTokenContext);

  const onFileUploadSuccess = async () => {
    enqueueSnackbar("Edition cover updated", { variant: "success" });
    await queryClient.invalidateQueries({ queryKey: MyGameEditions(gameId) });
    await queryClient.invalidateQueries({ queryKey: MyDoneGames });
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occurred uploading the edition cover", { variant: "error" });
  };

  const { games } = endpoints();
  const url = games().editionCover(gameId, editionId);
  const uploadImage = useUploadImage(s2Url(url), token, "cover", onFileUploadSuccess, onFileUploadFailure);

  return (
    <FilePond
      allowMultiple={false}
      allowReplace={false}
      allowRevert={false}
      allowProcess={false}
      credits={false}
      instantUpload={true}
      maxFiles={1}
      labelIdle={'Drag & Drop your edition cover or <span class="filepond--label-action"> Browse </span>'}
      allowImageResize
      imageResizeTargetWidth={1024}
      imageResizeTargetHeight={1024}
      maxFileSize="5MB"
      allowImageTransform
      imageResizeMode="contain"
      acceptedFileTypes={["image/png", "image/jpeg"]}
      fileValidateTypeLabelExpectedTypesMap={{ "image/png": "png", "image/jpeg": "jpg" }}
      server={{
        process: uploadImage,
      }}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").GameEditionId} props.editionId
 */
export const PremiumUploadGameEditionCover = ({ gameId, editionId }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const token = useContext(CachedTokenContext);

  const onFileUploadSuccess = async () => {
    enqueueSnackbar("Edition cover updated", { variant: "success" });
    await queryClient.invalidateQueries({ queryKey: MyGameEditions(gameId) });
    await queryClient.invalidateQueries({ queryKey: MyDoneGames });
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occurred uploading the edition cover", { variant: "error" });
  };

  const { games } = endpoints();
  const startUrl = games().editionCoverStart(gameId, editionId);
  const finishUrl = games().editionCoverFinish(gameId, editionId);
  const uploadImage = usePreSignedUploadImage(startUrl, finishUrl, token, onFileUploadSuccess, onFileUploadFailure);

  return (
    <FilePond
      allowMultiple={false}
      allowReplace={false}
      allowRevert={false}
      allowProcess={false}
      credits={false}
      instantUpload={true}
      maxFiles={1}
      labelIdle={'Drag & Drop your edition cover or <span class="filepond--label-action"> Browse </span>'}
      allowImageResize
      imageResizeTargetWidth={1024}
      imageResizeTargetHeight={1024}
      maxFileSize="500MB"
      allowImageTransform
      imageResizeMode="contain"
      acceptedFileTypes={["image/png", "image/jpeg"]}
      fileValidateTypeLabelExpectedTypesMap={{ "image/png": "png", "image/jpeg": "jpg" }}
      server={{
        process: uploadImage,
      }}
    />
  );
};
